import React, { useState, useEffect } from 'react'
import { Eye, Download, ChevronDown, RotateCcw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import AdminFilters from '../components/filters/AdminFilters'
import AdminPagination from '../components/pagination/AdminPagination'
import { useAdminFilters } from '../hooks/useAdminFilters'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import RefundModal from '../components/RefundModal'
import { downloadBlob } from '../lib/exportUtils'

interface PaymentItem {
  id: string
  productId: string
  product: { name: string; price: number }
  quantity: number
  price: number
}

interface Payment {
  id: string
  orderNumber: string
  razorpayPaymentId: string
  razorpayOrderId?: string
  user: { id: string; email: string; firstName: string; lastName: string }
  totalAmount: number
  paymentStatus: string
  paymentMethod: string
  createdAt: string | null
  updatedAt: string | null
  items?: PaymentItem[]
}

const PAYMENT_STATUSES = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'PAID', label: 'Paid' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'PARTIALLY_REFUNDED', label: 'Partially Refunded' },
]

export default function PaymentsPage() {
  const filterManager = useAdminFilters({ defaultLimit: 10 })
  const { search, debouncedSearch, page, limit, hasActiveFilters, setSearch, setPage, setLimit, setFilter, removeFilter, clearFilters } = filterManager

  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [expandedPaymentId, setExpandedPaymentId] = useState<string | null>(null)
  const [refundingPaymentId, setRefundingPaymentId] = useState<string | null>(null)
  const [selectedPaymentForRefund, setSelectedPaymentForRefund] = useState<Payment | null>(null)
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const navigate = useNavigate()

  const statusFilter = filterManager.filters.status || ''

  useEffect(() => {
    fetchPayments()
  }, [page, limit, debouncedSearch, JSON.stringify(filterManager.filters)])

  const fetchPayments = async () => {
    try {
      setLoading(true)
      const response = await api.getPayments(page, limit, debouncedSearch, { status: statusFilter })
      setPayments(response.data.data || [])
      setTotal(response.data.pagination?.total || 0)
    } catch (error) {
      toast.error('Failed to load payments')
    } finally {
      setLoading(false)
    }
  }

  const handleRefund = async (paymentId: string, amount: number) => {
    setRefundingPaymentId(paymentId)

    try {
      await api.refundPayment(paymentId, amount)
      toast.success('Payment refunded successfully')
      fetchPayments()
      setIsRefundModalOpen(false)
      setSelectedPaymentForRefund(null)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to refund payment')
    } finally {
      setRefundingPaymentId(null)
    }
  }

  const handleExportPayments = async () => {
    setIsExporting(true)

    try {
      const response = await api.exportPayments('', statusFilter)
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      downloadBlob(blob, 'payments-report.csv')
      toast.success('Payments exported successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to export payments')
    } finally {
      setIsExporting(false)
    }
  }

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-'

    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return '-'

      return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date)
    } catch {
      return '-'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-800'
      case 'PENDING':
        return 'bg-amber-100 text-amber-800'
      case 'FAILED':
        return 'bg-red-100 text-red-800'
      case 'REFUNDED':
      case 'PARTIALLY_REFUNDED':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    return PAYMENT_STATUSES.find((s) => s.value === status)?.label || status
  }

  const pages = Math.ceil(total / limit)

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#6D3A0D]">Payments</h1>
        <button
          onClick={handleExportPayments}
          disabled={isExporting || payments.length === 0}
          className="flex items-center gap-2 bg-[#6D3A0D] text-white px-4 py-2 rounded-lg hover:bg-[#5D2A0D] transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download size={20} />
          {isExporting ? 'Exporting...' : 'Export'}
        </button>
      </div>

      <RefundModal
        isOpen={isRefundModalOpen}
        payment={
          selectedPaymentForRefund
            ? {
                id: selectedPaymentForRefund.id,
                razorpayPaymentId: selectedPaymentForRefund.razorpayPaymentId,
                totalAmount: selectedPaymentForRefund.totalAmount,
                paymentStatus: selectedPaymentForRefund.paymentStatus,
              }
            : null
        }
        isLoading={refundingPaymentId !== null}
        onClose={() => {
          setIsRefundModalOpen(false)
          setSelectedPaymentForRefund(null)
          setRefundingPaymentId(null)
        }}
        onRefund={handleRefund}
      />

      <AdminFilters
        search={search}
        searchPlaceholder="Search payments by ID or customer..."
        onSearchChange={setSearch}
        filters={filterManager.filters}
        onFilterChange={setFilter}
        onRemoveFilter={removeFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        isLoading={loading}
        fields={[
          {
            key: 'status',
            label: 'Payment Status',
            type: 'select',
            options: PAYMENT_STATUSES,
          },
        ]}
      />

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#6D3A0D]"></div>
        </div>
      ) : payments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-[#F6EEE7]">
          <p className="text-gray-600">
            {hasActiveFilters ? 'No payments match your active filters.' : 'No payments found.'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 inline-flex items-center text-xs font-semibold text-[#6D3A0D] underline hover:text-[#5D2A0D]"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {payments.map((payment) => (
            <div key={payment.id} className="bg-white rounded-xl border-2 border-[#F6EEE7] shadow">
              <div
                onClick={() => setExpandedPaymentId(expandedPaymentId === payment.id ? null : payment.id)}
                className="p-6 cursor-pointer hover:bg-[#F6EEE7]/50 transition flex items-center justify-between"
              >
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase">Payment ID</p>
                    <p className="text-xs sm:text-sm font-bold text-gray-900 truncate">{payment.razorpayPaymentId?.slice(0, 16) || '-'}...</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase">Customer</p>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{payment.user?.email}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase">Amount</p>
                    <p className="text-xs sm:text-sm font-bold text-[#6D3A0D]">₹{parseFloat(payment.totalAmount.toString()).toFixed(2)}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase">Status</p>
                    <span className={`inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-semibold ${getStatusColor(payment.paymentStatus)}`}>
                      {getStatusLabel(payment.paymentStatus)}
                    </span>
                  </div>
                </div>
                <ChevronDown
                  size={20}
                  className={`transition-transform ${expandedPaymentId === payment.id ? 'rotate-180' : ''}`}
                />
              </div>

              {expandedPaymentId === payment.id && (
                <div className="border-t-2 border-[#F6EEE7] p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Order Number</p>
                      <p className="text-lg font-bold text-gray-900">{payment.orderNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Payment Method</p>
                      <p className="text-lg font-bold text-gray-900">{payment.paymentMethod}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Payment Date</p>
                      <p className="text-lg text-gray-900">{formatDate(payment.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Customer</p>
                      <p className="text-lg text-gray-900">
                        {payment.user?.firstName} {payment.user?.lastName}
                      </p>
                    </div>
                  </div>

                  {/* Payment IDs Section */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">Payment ID</p>
                      <p className="text-xs sm:text-sm font-mono text-gray-900 break-all">{payment.razorpayPaymentId || '-'}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">Razorpay Order ID</p>
                      <p className="text-xs sm:text-sm font-mono text-gray-900 break-all">{payment.razorpayOrderId || '-'}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">Transaction ID</p>
                      <p className="text-xs sm:text-sm font-mono text-gray-900 break-all">{payment.id}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Payment Items</h4>
                    <div className="space-y-2">
                      {payment.items?.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm text-gray-600 bg-[#F6EEE7] p-2 rounded">
                          <span>{item.product?.name} × {item.quantity}</span>
                          <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4 pt-4 border-t border-gray-100">
                    {(payment.paymentStatus === 'PAID' || payment.paymentStatus === 'PARTIALLY_REFUNDED') && (
                      <button
                        onClick={() => {
                          setSelectedPaymentForRefund(payment)
                          setIsRefundModalOpen(true)
                        }}
                        disabled={refundingPaymentId === payment.id}
                        className="flex items-center gap-2 bg-[#6D3A0D] text-white px-4 py-2 rounded-lg hover:bg-[#5D2A0D] transition font-semibold text-sm disabled:opacity-50"
                      >
                        <RotateCcw size={16} />
                        Process Refund
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/orders/${payment.id}`)}
                      className="flex items-center gap-2 border-2 border-[#F6EEE7] text-gray-700 px-4 py-2 rounded-lg hover:bg-[#F6EEE7] transition font-semibold text-sm"
                    >
                      <Eye size={16} />
                      View Order
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {total > 0 && (
        <div className="mt-6">
          <AdminPagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={loading}
            showInfo={true}
          />
        </div>
      )}
    </div>
  )
}
